<template>
  <v-container>
    <page-heading>{{ pageTitle }}</page-heading>

    <info-alert v-if="isOnlyNewMode">{{ trans('booking.newBookingsHelp') }}</info-alert>

      <ajax-table
        ref="bookingsTable"
        :resource="resourceUrl"
        :headers="[
          {text: 'booking.bookingId', value: 'id'},
          {text: 'booking.status', value: 'status'},
          {text: 'booking.bookingDate', value: 'bookingDate'},
          {text: 'object.object', value: 'objectName'},
          {text: 'booking.checkinAt', value: 'checkinAt'},
          {text: 'booking.checkoutAt', value: 'checkoutAt'},
          {text: 'guest.guest', value: 'guestName'},
          {text: 'guest.guests', value: 'guests'},
          {text: 'booking.rent', value: 'totalWithoutDepositAndBookingFee', type: 'currency'},
        ]"
        :page-options="{itemsPerPage: 20, sortBy: initSort.sortBy, sortDesc: initSort.sortDesc}"
        :items-per-page-options="[10, 20, 50, 100]"
        :params="{onlyNew: isOnlyNewMode}"
        :filter="bookingsFilter"
        exportable
      >
        <template v-slot:filters v-if="!isOnlyNewMode">
          <v-col cols="6" sm="6" md="4" offset-lg="9" lg="3">
            <search v-model="bookingsFilterData.search" width="auto" />
          </v-col>
          <v-col cols="6" sm="6" md="4" lg="3">
            <date-period-select
              v-model="bookingsFilterData.bookingDate"
              :label="trans('booking.bookingDate')"
            />
          </v-col>
          <v-col cols="6" sm="6" md="4" lg="3">
            <date-period-select
              v-model="bookingsFilterData.checkIn"
              :label="trans('booking.checkinAt')"
            />
          </v-col>
          <v-col cols="6" sm="6" md="4" lg="3">
            <date-period-select
              v-model="bookingsFilterData.checkOut"
              :label="trans('booking.checkoutAt')"
            />
          </v-col>
          <v-col cols="6" sm="6" md="4" lg="3" v-if="canSeeAllDetails">
            <v-select
              v-model="bookingsFilterData.deleted"
              :label="trans('booking.showDeleted')"
              :items="showDeletedStatus"
              hide-details
            />
          </v-col>
          <v-col cols="6" sm="6" md="4" lg="3">
            <select-object
              :object-id.sync="bookingsFilterData.objectIds"
              :multiple="true"
              clearable
              hide-details
            />
          </v-col>
          <v-col cols="6" sm="6" md="4" lg="3">
            <select-object-city
              :value.sync="bookingsFilterData.objectCities"
              clearable
              hide-details
            />
          </v-col>
          <v-col cols="6" sm="6" md="4" lg="3">
            <v-select
                v-model="bookingsFilterData.status"
                :label="trans('booking.status')"
                :items="[0, 1]"
                hide-details
                clearable
            >
              <template v-slot:selection="{item}">
                <booking-status :value="item" />
              </template>

              <template v-slot:item="{item, on, attrs}">
                <v-list-item v-bind="attrs" v-on="on">
                  <v-list-item-content>
                    <v-list-item-title>
                      <booking-status :value="item" />
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

              </template>
            </v-select>
          </v-col>
          <v-col cols="6" sm="6" md="4" lg="3" v-if="canSeeAllDetails">
            <v-select
                v-model="bookingsFilterData.onlyCanceledInStayin"
                :label="trans('booking.filterOnlyCanceledInStayin')"
                :items="onlyCanceledInStayinOptions"
                hide-details
            />
          </v-col>
          <v-col cols="6" sm="6" md="4" lg="3" v-if="!canSeeAllDetails">
            <!-- Below filter shows bookings which are canceled in stayin, this is only interesting for caretakers -->
            <v-select
              v-model="bookingsFilterData.anyCanceled"
              :label="transPlural('booking.cancellation')"
              :items="anyCanceledOptions"
              hide-details
              clearable
            />
          </v-col>
        </template>

        <template v-slot:item.id="{ item }">
          <booking-id :value="item" :open-in-new-tab="false" />
        </template>

        <template v-slot:item.status="{ item }">
          <status-badge v-if="item.disabled" color="red lighten-3">{{ trans('general.deleted') }}</status-badge>
          <template v-else>
            <booking-only-canceled-in-stayin v-if="canSeeAllDetails && isOnlyCanceledInStayin(item)" />
            <booking-status :booking="item"/>
          </template>
        </template>

        <template v-slot:item.objectName="{ item }">
          <object-name :value="item" />
        </template>

        <template v-slot:item.guestName="{ item }">
          <guest-name :value="item"/>
        </template>

        <template v-slot:item.guests="{ item }">
          <booking-guests-summary :booking="item"></booking-guests-summary>
        </template>
      </ajax-table>
  </v-container>
</template>

<script>
import AjaxTable from '../components/AjaxTable';
import {LocaleUtils} from '../utils/locale-utils';
import PageHeading from '../components/PageHeading';
import {DateUtils} from '../utils/date-utils';
import {RouterUtils} from '../utils/router-utils';
import {MainUtils} from '../utils/main-utils';
import {comparison, eq, inList, or} from 'rsql-builder';
import SelectObject from '@/components/SelectObject';
import Search from '@/components/Search';
import DatePeriodSelect from '@/components/DatePeriodSelect';
import ObjectName from '@/components/ObjectName';
import StatusBadge from '@/components/StatusBadge';
import {BookingApi} from '@/api/booking-api';
import {BookingPageModes} from '@/pages/PageModes';
import {FilterUtils} from '@/utils/filter-utils';
import SelectObjectCity from '@/components/SelectObjectCity';
import BookingId from '@/components/BookingId';
import BookingStatus from '@/components/BookingStatus';
import GuestName from "@/components/GuestName";
import InfoAlert from '../components/InfoAlert';
import BookingGuestsSummary from '@/components/BookingGuestsSummary';
import BookingOnlyCanceledInStayin from '../components/icons/BookingOnlyCanceledInStayin.vue';

export default {
  name: 'BookingsPage',
  mixins: [
    LocaleUtils,
    DateUtils,
    RouterUtils,
    MainUtils,
    FilterUtils
  ],
  components: {
    BookingOnlyCanceledInStayin,
    BookingGuestsSummary,
    InfoAlert,
    BookingStatus,
    BookingId,
    SelectObjectCity,
    StatusBadge,
    ObjectName,
    DatePeriodSelect,
    Search,
    SelectObject,
    PageHeading,
    AjaxTable,
    GuestName
  },
  props: [
    'viewMode'
  ],
  data() { return {
    bookingsFilterData: {
      deleted: false,
      onlyCanceledInStayin: this.$route?.query?.onlyCanceledInStayin || null, // Show canceled in staying, but not in office
      anyCanceled: null, // Show canceled in stayin
      status: null,
      objectIds: [],
      objectCities: [],
      search: null,
      bookingDate: null,
      checkIn: this.viewMode === BookingPageModes.CHECK_IN ? this.getDateRangeStringToEndOfNextMonth() : null,
      checkOut: this.viewMode === BookingPageModes.CHECK_OUT ? this.getDateRangeStringToEndOfNextMonth() : null
    },
    onlyNewArrivals: false
  }},

  computed: {
    initSort() {
      switch (this.viewMode) {
        case BookingPageModes.CHECK_IN: return {sortBy: ['checkinAt'], sortDesc: [false]};
        case BookingPageModes.CHECK_OUT: return {sortBy: ['checkoutAt'], sortDesc: [false]};
        case BookingPageModes.ONLY_NEW:
        default: return {sortBy: ['id'], sortDesc: [true]};
      }
    },
    showDeletedStatus () {
      return [
        {
          value: false,
          text: this.trans('booking.showDeletedNo'),
        },
        {
          value: null,
          text: this.trans('booking.showDeletedYes'),
        },
        {
          value: true,
          text: this.trans('booking.showDeletedOnly'),
        }
      ]
    },
    onlyCanceledInStayinOptions () {
      return [
        {
          value: null,
          text: this.trans('general.no'),
        },
        {
          value: true,
          text: this.trans('general.yes'),
        }
      ]
    },
    anyCanceledOptions () {
      return [
        {
          value: true,
          text: this.trans('booking.showCanceledOnly'),
        }
      ]
    },
    bookingsFilter () {
      let filters = [];
      if(this.bookingsFilterData.deleted != null) {
        filters.push(comparison('disabled', eq(this.bookingsFilterData.deleted ? 1 : 0)));
      }
      if(this.bookingsFilterData.status != null) {
        filters.push(comparison('status', eq(this.bookingsFilterData.status)));
      }
      if(this.bookingsFilterData.objectIds != null && this.bookingsFilterData.objectIds.length > 0) {
        filters.push(comparison('object.id', inList(...this.bookingsFilterData.objectIds)));
      }
      if(this.bookingsFilterData.objectCities != null && this.bookingsFilterData.objectCities.length > 0) {
        filters.push(comparison('object.city', inList(...this.bookingsFilterData.objectCities)));
      }
      if(this.bookingsFilterData.search) {
        filters.push(this.buildSearchFilter(this.bookingsFilterData.search, ['object.name', 'guest.firstName', 'guest.lastName', 'id']))
      }
      if(this.bookingsFilterData.bookingDate != null) {
        filters.push(this.buildDateTimeFilter('bookingDate', this.bookingsFilterData.bookingDate));
      }
      if(this.bookingsFilterData.checkIn != null) {
        filters.push(this.buildDateFilter('checkinAt', this.bookingsFilterData.checkIn));
      }
      if(this.bookingsFilterData.checkOut != null) {
        filters.push(this.buildDateFilter('checkoutAt', this.bookingsFilterData.checkOut));
      }
      if(this.bookingsFilterData.onlyCanceledInStayin != null) {
        filters.push(comparison('onlyCanceledInStayin', eq(this.bookingsFilterData.onlyCanceledInStayin)));
      }
      if(this.bookingsFilterData.anyCanceled != null) {
        filters.push(or(comparison('stayinStatus', eq('CANCELED')), comparison('canceled', eq(true))));
      }
      // Only new arrivals filter is set as query param
      return filters;
    },
    canSeeAllDetails() {
      return this.hasAnyPermission('BOOKING_VIEW_DETAILS');
    },
    resourceUrl () {
      return BookingApi.apiUri;
    },
    isCheckinMode () {
      return this.viewMode === BookingPageModes.CHECK_IN;
    },
    isOnlyNewMode () {
      return this.viewMode === BookingPageModes.ONLY_NEW;
    },
    pageTitle () {
      return this.isOnlyNewMode
        ? this.trans('booking.newBookings')
        : this.transPlural('booking.booking');
    }
  },

  mounted () {
    this.resetSearchFilters();
  },

  activated () {
    this.$refs.bookingsTable.loadData();
  },

  methods: {
    resetSearchFilters () {
      this.bookingsFilterData.deleted = false;
      this.bookingsFilterData.status = null;
      this.bookingsFilterData.objectId = [];
      this.bookingsFilterData.objectCities = [];
      this.bookingsFilterData.search = null;
      this.bookingsFilterData.bookingDate = null;
      this.bookingsFilterData.checkIn = this.viewMode === BookingPageModes.CHECK_IN ? this.getDateRangeStringToEndOfNextMonth() : null;
      this.bookingsFilterData.checkOut = this.viewMode === BookingPageModes.CHECK_OUT ? this.getDateRangeStringToEndOfNextMonth() : null;
    },
    getDateRangeStringToEndOfNextMonth () {
      return [this.getIsoDateString(), this.getEndOfNextMonthString()];
    },
    isOnlyCanceledInStayin (booking) {
      return booking.canceled === false && booking.stayinCanceled === true
    }
  },

  watch: {
    onlyNewArrivals (onlyNewArrivals) {
      if (onlyNewArrivals) {
        this.resetSearchFilters();
      }
    }
  }
}
</script>

<style>

</style>
